import { StandaloneFlightMenu } from 'contexts/Flights/FlightSearchWidget/types'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { FlightsFareTypes } from 'constants/flight'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import type { SelectingDateType } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import type { MutableRefObject } from 'react'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'

interface Props {
  className?: string
  id: string
  departingFieldRef?: MutableRefObject<HTMLButtonElement | null> | ((node: HTMLButtonElement | null) => void)
  arrivalFieldRef?: | MutableRefObject<HTMLButtonElement | null> | ((node: HTMLButtonElement | null) => void)
  onDepartingClick?: () => void
  onArrivalClick?: () => void
}

function FlightSearchDateSelect(props: Props) {
  const {
    id,
    departingFieldRef,
    arrivalFieldRef,
    onArrivalClick,
  } = props
  const intl = useIntl()
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)

  const currentFlight = useMemo(() => {
    return state.flights.find((flight) => flight.id === id)
  }, [state.flights, id])

  const checkinDate = currentFlight?.checkinDate
  const checkoutDate = currentFlight?.checkoutDate

  const { fareType } = state

  const setActiveMenu = useCallback(
    (activeMenu?: StandaloneFlightMenu, id?: string, selectingDateType?: SelectingDateType) => {
      dispatch({
        type: FlightSearchWidgetActions.SET_ACTIVE_MENU, id, activeMenu, selectingDateType,
      })
    },
    [dispatch],
  )

  const handleDepartingClick = useCallback(() => {
    setActiveMenu(StandaloneFlightMenu.date, id, 'departing')
  }, [setActiveMenu, id])

  const handleReturningClick = useCallback(() => {
    setActiveMenu(StandaloneFlightMenu.date, id, 'returning')
    onArrivalClick?.()
  }, [setActiveMenu, onArrivalClick, id])

  const formattedCheckInDate = useMemo(() => {
    return checkinDate?.isValid() ? intlDateFormatter(checkinDate, INTL_DMY_CASUAL_FORMAT, intl.locale) : undefined
  }, [checkinDate, intl.locale])

  const formattedCheckOutDate = useMemo(() => {
    return checkoutDate?.isValid() ? intlDateFormatter(checkoutDate, INTL_DMY_CASUAL_FORMAT, intl.locale) : undefined
  }, [checkoutDate, intl.locale])

  useEffect(() => {
    if (fareType.value === FlightsFareTypes.ONE_WAY) {
      dispatch({
        type: FlightSearchWidgetActions.SET_CHECKOUT_DATE,
        date: null,
      })
    }
  }, [dispatch, fareType.value])

  const departingLabel = useMemo(
    () =>
      intl.formatMessage({
        id: 'h715GKJI_T7chJO4FOob',
        defaultMessage: 'Departing',
        description: 'Form field label for departing date field in flight search',
      }),
    [intl],
  )

  const returningLabel = useMemo(
    () =>
      intl.formatMessage({
        id: 'wM-9lNMdAZJhxb1b-Ntf',
        defaultMessage: 'Returning',
        description: 'Form field label for returning date field in flight search',
      }),
    [intl],
  )

  const selectDatePlaceholder = useMemo(
    () =>
      intl.formatMessage({
        id: 'WgFH00uyPfLjuOWs59-L',
        defaultMessage: 'Select date',
        description: 'Placeholder text for date selection field in flight search',
      }),
    [intl],
  )

  const selectDateErrorMessage = useMemo(
    () =>
      intl.formatMessage({
        id: 'mV2FO-4WIzaCY7H94ZNu',
        defaultMessage: 'Please select travel dates',
        description: 'Error message when travel dates are not selected in flight search',
      }),
    [intl],
  )

  return (
    <SearchFormFieldGroup>
      <SearchFormField
        ref={departingFieldRef}
        label={departingLabel}
        placeholder={selectDatePlaceholder}
        required
        requiredErrorMessage={selectDateErrorMessage}
        value={formattedCheckInDate}
        onClick={handleDepartingClick}
        name={`departing-${id}`}
      />

      {fareType.value === FlightsFareTypes.RETURN && (
        <SearchFormField
          ref={arrivalFieldRef}
          label={returningLabel}
          placeholder={selectDatePlaceholder}
          required
          requiredErrorMessage={selectDateErrorMessage}
          value={formattedCheckOutDate}
          onClick={handleReturningClick}
          name={`returning-${id}`}
        />
      )}
    </SearchFormFieldGroup>
  )
}

export default FlightSearchDateSelect
